import { useParams, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { tournamentService, tournamentStageService } from "@/frontend/services";
import type { TournamentResponseDTO } from "@/shared/dto";
import { useGetId } from "@/frontend/hooks/useGetId";
import { Card, Spin, Tabs } from "@/frontend/components/common";
import { FaTrophy, FaUsers, FaCalendarAlt, FaFutbol, FaBroadcastTower, FaCog, FaClipboardList, FaUserShield } from "react-icons/fa";
import type { SeasonInfo, TournamentStageInfo } from "./types";
import type { StageConfig } from "@/core/value-objects/StageConfig";
import { TournamentHeader } from "./components";
import { OverviewTab, FixturesTab, LiveFixturesTab, GroupsTab, KnockoutTab, FormatTab, TeamPlayersTab } from "./tabs";
import { TournamentForm } from "../components/TournamentForm";
import dayjs from "dayjs";

export function TournamentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: tournament, loading } = useGetId<TournamentResponseDTO>({
    fetchFn: tournamentService.getById,
    redirectUrl: "/tournaments",
    errorMessage: "Error al cargar el torneo",
    id,
  });

  const currentSeason = useMemo<SeasonInfo | undefined>(() => {
    return tournament?.seasons?.find((s) => s.isCurrent) as SeasonInfo | undefined;
  }, [tournament]);

  const [tournamentStages, setTournamentStages] = useState<TournamentStageInfo[]>([]);
  const [formatStagesOverride, setFormatStagesOverride] = useState<StageConfig[] | null>(null);

  useEffect(() => {
    const fetchTournamentStages = async () => {
      if (!currentSeason?.id) return;

      try {
        const response = await tournamentStageService.getBySeasonId(currentSeason.id);
        if (response.success && response.data) {
          setTournamentStages(response.data);
        }
      } catch (error) {
        console.error("Error fetching tournament stages:", error);
      }
    };

    fetchTournamentStages();
  }, [currentSeason?.id]);

  useEffect(() => {
    setFormatStagesOverride(null);
  }, [currentSeason?.formatId]);

  const stages = useMemo(() => {
    if (formatStagesOverride) {
      return [...formatStagesOverride].sort((a, b) => a.order - b.order);
    }
    if (!currentSeason?.format?.config?.stages) return [];
    return [...currentSeason.format.config.stages].sort(
      (a, b) => a.order - b.order
    );
  }, [currentSeason, formatStagesOverride]);

  const [activeKey, setActiveKey] = useState<string>("overview");

  useEffect(() => {
    if (tournament?.canEdit) setActiveKey("configurar");
  }, [tournament?.canEdit]);

  const allKnockoutStages = useMemo(
    () => stages.filter((s) => s.type === "KNOCKOUT"),
    [stages]
  );

  const groupStages = useMemo(
    () => stages.filter((s) => s.type === "GROUPS"),
    [stages]
  );

  const otherStages = useMemo(
    () => stages.filter((s) => s.type !== "KNOCKOUT" && s.type !== "GROUPS"),
    [stages]
  );

  const requiresFairPlay = Boolean(tournament?.configuration?.hasCards);

  // Must be before any conditional return — React hooks rule
  const configurarInitialValues = useMemo(() => {
    if (!tournament?.canEdit) return undefined;
    const activeSeason =
      tournament.seasons?.find((s) => s.isCurrent) ?? tournament.seasons?.[0];
    const seasonStart = activeSeason?.start
      ? new Date(activeSeason.start).toISOString().slice(0, 10)
      : undefined;
    const seasonEnd = activeSeason?.end
      ? new Date(activeSeason.end).toISOString().slice(0, 10)
      : undefined;
    return {
      name: tournament.name,
      discipline: tournament.discipline
        ? { id: tournament.discipline.id, name: tournament.discipline.name }
        : undefined,
      description: tournament.description || "",
      sortOrder: tournament.sortOrder ?? undefined,
      country: tournament.country
        ? { id: tournament.country.id, name: tournament.country.name }
        : undefined,
      configuration: {
        hasReferees: tournament.configuration?.hasReferees ?? false,
        hasLineups: tournament.configuration?.hasLineups ?? false,
        hasStadium: tournament.configuration?.hasStadium ?? false,
        hasTeams: tournament.configuration?.hasTeams ?? true,
        hasSubstitutions: tournament.configuration?.hasSubstitutions ?? false,
        hasCards: tournament.configuration?.hasCards ?? false,
        maxPlayersPerTeam: tournament.configuration?.maxPlayersPerTeam,
        minPlayersPerTeam: tournament.configuration?.minPlayersPerTeam,
        eventTypes: (tournament.configuration?.eventTypes || []).join(","),
      },
      statusIds: (tournament.statusIds as string[]) || [],
      statusId:
        tournament.statusIds && tournament.statusIds.length > 0
          ? tournament.statusIds[0]
          : undefined,
      tournamentRange:
        seasonStart && seasonEnd
          ? [dayjs(seasonStart), dayjs(seasonEnd)]
          : undefined,
      tournamentStart: seasonStart,
      tournamentEnd: seasonEnd,
      seasons: tournament.seasons?.map((s) => ({
        id: (s as any).id,
        season: s.season,
        start: s.start ? new Date(s.start).toISOString().slice(0, 10) : undefined,
        end: s.end ? new Date(s.end).toISOString().slice(0, 10) : undefined,
        statusId: s.status?.id,
        status: s.status ?? null,
        isCurrent: s.isCurrent ?? false,
        formatId: (s as SeasonInfo).formatId ?? (s as SeasonInfo).format?.id,
      })),
      newSeasons: [],
    };
  }, [tournament?.id]);

  if (loading) {
    return (
      <div className="w-full min-h-[60vh] flex items-center justify-center">
        <Spin size="large" tip="Cargando torneo..." />
      </div>
    );
  }

  if (!tournament) return null;

  const tabItems = [
    ...(tournament.canEdit && configurarInitialValues
      ? [
          {
            key: "configurar",
            label: (
              <span className="flex items-center gap-2">
                <FaCog className="text-gray-500" />
                Configurar
              </span>
            ),
            children: (
              <div className="py-4">
                <TournamentForm
                  tournamentId={tournament.id}
                  initialValues={configurarInitialValues as any}
                />
              </div>
            ),
          },
        ]
      : []),
    ...(tournament.canEdit && currentSeason
      ? [
          {
            key: "formato-equipos",
            label: (
              <span className="flex items-center gap-2">
                <FaClipboardList className="text-indigo-500" />
                Formato y Equipos
              </span>
            ),
            children: (
              <FormatTab
                tournamentId={tournament.id}
                currentSeason={currentSeason}
                stages={stages}
                tournamentStages={tournamentStages}
                onStagesChange={setFormatStagesOverride}
              />
            ),
          },
        ]
      : []),
    {
      key: "overview",
      label: (
        <span className="flex items-center gap-2">
          <FaTrophy className="text-amber-500" />
          Resumen
        </span>
      ),
      children: (
        <OverviewTab tournament={tournament} currentSeason={currentSeason} />
      ),
    },
    {
      key: "fixtures",
      label: (
        <span className="flex items-center gap-2">
          <FaCalendarAlt className="text-green-500" />
          Partidos
        </span>
      ),
      children: (
        <FixturesTab
          tournamentId={tournament.id}
          currentSeason={currentSeason}
          tournamentStages={tournamentStages}
          requiresFairPlay={requiresFairPlay}
          autoCategories={tournament.autoCategories ?? []}
        />
      ),
    },
    ...(tournament.canEdit && currentSeason
      ? [
          {
            key: "team-players",
            label: (
              <span className="flex items-center gap-2">
                <FaUserShield className="text-purple-500" />
                Jugadores
              </span>
            ),
            children: (
              <TeamPlayersTab
                tournamentId={tournament.id}
                currentSeason={currentSeason}
                tournamentStages={tournamentStages}
              />
            ),
          },
        ]
      : []),
    {
      key: "live-fixtures",
      label: (
        <span className="flex items-center gap-2">
          <FaBroadcastTower className="text-red-500" />
          Partidos en vivo
        </span>
      ),
      children: <LiveFixturesTab currentSeason={currentSeason} />,
    },
    // Tabs de grupos
    ...groupStages.map((stage) => ({
      key: `stage-${stage.order}`,
      label: (
        <span className="flex items-center gap-2">
          <FaUsers className="text-blue-500" />
          {stage.name}
        </span>
      ),
      children: currentSeason ? (
        <GroupsTab
          stage={stage}
          currentSeason={currentSeason}
          stages={stages}
          tournamentStages={tournamentStages}
        />
      ) : null,
    })),
    // Otras etapas (playoff, etc.)
    ...otherStages.map((stage) => ({
      key: `stage-${stage.order}`,
      label: (
        <span className="flex items-center gap-2">
          <FaFutbol className="text-green-500" />
          {stage.name}
        </span>
      ),
      children: (
        <div className="py-4">
          <Card className="shadow-sm">
            <div className="text-center py-8">
              <p className="text-gray-400 mt-8 italic">
                El contenido de esta etapa se implementará próximamente
              </p>
            </div>
          </Card>
        </div>
      ),
    })),
    // Tab contenedor de Eliminatorias
    ...(allKnockoutStages.length && currentSeason
      ? [
          {
            key: "knockouts",
            label: (
              <span className="flex items-center gap-2">
                <FaTrophy className="text-red-500" />
                Eliminatorias
              </span>
            ),
            children: (
              <KnockoutTab
                stages={stages}
                currentSeason={currentSeason}
                tournamentStages={tournamentStages}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="w-full space-y-6">
      <TournamentHeader tournament={tournament} currentSeason={currentSeason} />

      <Card className="shadow-md">
        <Tabs
          defaultActiveKey="overview"
          activeKey={activeKey}
          onChange={(key) => setActiveKey(key as string)}
          items={tabItems}
          size="large"
          className="tournament-stages-tabs"
        />
      </Card>
    </div>
  );
}
